/* eslint-disable @typescript-eslint/no-misused-promises */
import { Injectable, inject, ApplicationRef, NgZone } from "@angular/core";
import { first, interval } from "rxjs";
import { ServiceWorkerMessage, ServiceWorkerUpdateInfo } from "./notification-interfaces";
import { LoggerService } from "src/ancestors/logger.service";

/**
 * Servizio per la gestione automatica e silenziosa degli aggiornamenti del Service Worker
 * Controlla periodicamente la presenza di aggiornamenti e li applica automaticamente
 * senza interazione dell'utente.
 */
@Injectable({
  providedIn: "root"
})
export class SilentSwUpdateService {
  private appRef = inject(ApplicationRef);
  private ngZone = inject(NgZone);
  private logger = inject(LoggerService);

  private isInitialized = false;
  private checkInterval = 5 * 60 * 1000; // 5 minuti in millisecondi
  private maxRetries = 3;
  private retryDelay = 10000; // 10 secondi
  private stabilityTimeout = 10000; // 10 secondi timeout per app stability
  private serviceWorkerReadyTimeout = 15000; // 15 secondi timeout per SW ready

  /**
   * Inizializza il servizio di aggiornamento automatico
   * Usa un timeout per evitare di bloccarsi sui dialog delle notifiche
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Prova ad aspettare che l'app sia stabile, ma con timeout
      this.logger.debug("Waiting for app to be stable (with timeout)");
      const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));

      const isStablePromise = new Promise<void>((resolve) => {
        appIsStable$.subscribe(() => {
          this.logger.debug("App is stable, proceeding with SW initialization");
          resolve();
        });
      });

      // Timeout dopo 10 secondi - procedi comunque con l'inizializzazione SW
      const timeoutPromise = new Promise<void>((resolve) => {
        setTimeout(() => {
          this.logger.debug("App stability timeout reached, proceeding with SW initialization anyway");
          resolve();
        }, this.stabilityTimeout); // Timeout configurabile
      });

      // Aspetta che l'app sia stabile O che scada il timeout
      await Promise.race([isStablePromise, timeoutPromise]);

      this.logger.debug("Starting silent update monitoring");
      await this.startSilentUpdateMonitoring();
      this.isInitialized = true;

      // Silent update service initialized correctly
    } catch (error) {
      // Silent error handling
    }
  }

  /**
   * Avvia il monitoraggio automatico degli aggiornamenti
   * Controlla periodicamente la presenza di nuove versioni del SW
   */
  private async startSilentUpdateMonitoring(): Promise<void> {
    if (!("serviceWorker" in navigator)) {
      this.logger.debug("Service worker not supported");
      return;
    }

    try {
      // Controllo iniziale degli aggiornamenti
      this.logger.debug("Performing initial update check");
      await this.performInitialUpdateCheck();

      // Avvia il controllo periodico
      this.logger.debug("Starting periodic update checks");
      this.startPeriodicUpdateChecks();

      // Automatic update monitoring started
    } catch (error) {
      // Silent error handling
    }
  }

  /**
   * Esegue un controllo iniziale per aggiornamenti disponibili
   */
  private async performInitialUpdateCheck(): Promise<void> {
    try {
      this.logger.debug("Performing initial update check");
      const hasUpdate = await this.checkForServiceWorkerVersion();

      if (hasUpdate) {
        this.logger.debug("Handle silent update");
        await this.handleSilentUpdate();
      }
    } catch (error) {
      // Silent error handling
    }
  }

  /**
   * Avvia i controlli periodici per aggiornamenti
   */
  private startPeriodicUpdateChecks(): void {
    this.ngZone.runOutsideAngular(() => {
      interval(this.checkInterval).subscribe(async () => {

        try {
          this.logger.debug("Checking for service worker version");
          const hasUpdate = await this.checkForServiceWorkerVersion();

          if (hasUpdate) {
            this.logger.debug("Handle silent update");
            await this.handleSilentUpdate();
          }
        } catch (error) {
          // Silent error handling
        }
      });
    });
  }

  /**
   * Gestisce l'aggiornamento silenzioso del Service Worker
   * Tenta l'aggiornamento con retry in caso di errore
   */
  private async handleSilentUpdate(): Promise<void> {

    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {


        // Verifica che il SW sia pronto
        this.logger.debug("Testing service worker connection");
        const isConnected = await this.testServiceWorkerConnection();
        if (!isConnected) {
          throw new Error("Service Worker non risponde");
        }

        // Ottieni informazioni sulla versione corrente
        const currentVersion = await this.checkForServiceWorkerVersion();
        if (currentVersion) {
          this.logger.debug(`Current SW version retrieved: ${currentVersion}`);
          // Current SW version retrieved
        }

        // Forza l'aggiornamento
        await this.forceServiceWorkerUpdate();

        // Aspetta un po' e verifica la nuova versione
        setTimeout(async () => {
          try {
            const newVersion = await this.checkForServiceWorkerVersion();
            if (newVersion) {
              // New SW version is now active
              this.logger.debug("New SW version is now active");
            }
          } catch (error) {
            // Silent error handling
          }
        }, 2000);

        return; // Successo, esce dal loop di retry

      } catch (error) {
        if (attempt === this.maxRetries) {
          return;
        }

        // Aspetta prima del prossimo tentativo
        await this.delay(this.retryDelay);
      }
    }
  }

  /**
   * Forza un controllo manuale degli aggiornamenti (per debug)
   */
  async forceUpdateCheck(): Promise<void> {
    try {
      this.logger.debug("Performing force update check");
      const hasUpdate = await this.checkForServiceWorkerVersion();

      if (hasUpdate) {
        this.logger.debug("Handle silent update");
        await this.handleSilentUpdate();
      }
    } catch (error) {
      // Silent error handling
    }
  }

  /**
   * Ottiene lo stato corrente del Service Worker
   */
  async getCurrentStatus(): Promise<{ connected: boolean, version: string | null }> {
    try {
      this.logger.debug("Performing force update check");
      const connected = await this.testServiceWorkerConnection();
      this.logger.debug(`Connected: ${connected}`);
      const versionInfo = await this.getServiceWorkerVersion();
      this.logger.debug(`Version info: ${JSON.stringify(versionInfo)}`);

      const status = {
        connected,
        version: versionInfo ? `${versionInfo.version} (${versionInfo.buildTime})` : null
      };

      return status;
    } catch (error) {
      return { connected: false, version: null };
    }
  }

  /**
   * Configura l'intervallo di controllo aggiornamenti
   * @param intervalMinutes - Intervallo in minuti tra i controlli
   */
  setUpdateCheckInterval(intervalMinutes: number): void {
    if (intervalMinutes < 1) {
      this.logger.debug("Invalid update check interval");
      return;
    }

    this.checkInterval = intervalMinutes * 60 * 1000;
    this.logger.debug(`Update check interval set to: ${this.checkInterval}ms`);
  }

  /**
   * Configura il timeout per l'attesa della stabilità dell'app
   * @param timeoutSeconds - Timeout in secondi per aspettare l'app stability
   */
  setStabilityTimeout(timeoutSeconds: number): void {
    if (timeoutSeconds < 1) {
      this.logger.debug("Invalid stability timeout");
      return;
    }

    this.stabilityTimeout = timeoutSeconds * 1000;
    this.logger.debug(`Stability timeout set to: ${this.stabilityTimeout}ms`);
  }

  /**
   * Configura il timeout per l'attesa del service worker ready
   * @param timeoutSeconds - Timeout in secondi per aspettare il service worker ready
   */
  setServiceWorkerReadyTimeout(timeoutSeconds: number): void {
    if (timeoutSeconds < 1) {
      this.logger.debug("Invalid service worker ready timeout");
      return;
    }

    this.serviceWorkerReadyTimeout = timeoutSeconds * 1000;
    this.logger.debug(`Service worker ready timeout set to: ${this.serviceWorkerReadyTimeout}ms`);
  }

  /**
   * Utility per delay asincrono
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Wrapper per navigator.serviceWorker.ready con timeout
   * Evita che il service worker si blocchi sui dialog delle notifiche
   * @param timeoutMs - Timeout in millisecondi (default: usa valore configurato)
   * @returns Promise con ServiceWorkerRegistration o null se timeout
   */
  private async getServiceWorkerReady(timeoutMs?: number): Promise<ServiceWorkerRegistration | null> {
    if (!("serviceWorker" in navigator)) {
      this.logger.debug("Service worker not supported");
      return null;
    }

    try {
      const readyPromise = navigator.serviceWorker.ready;
      const timeoutPromise = new Promise<null>((resolve) => {
        setTimeout(() => {
          this.logger.debug("Service worker ready timeout reached");
          resolve(null);
        }, timeoutMs ?? this.serviceWorkerReadyTimeout);
      });

      const result = await Promise.race([readyPromise, timeoutPromise]);
      
      if (result === null) {
        this.logger.debug("Service worker ready timed out");
        return null;
      }

      this.logger.debug("Service worker is ready");
      return result;
    } catch (error) {
      this.logger.debug(`Error waiting for service worker ready: ${error instanceof Error ? error.message : String(error)}`);
      return null;
    }
  }

  private async checkForServiceWorkerVersion(): Promise<boolean> {
    try {
      // Use timeout wrapper instead of direct navigator.serviceWorker.ready
      this.logger.debug("Checking for service worker version with timeout");
      const registration = await this.getServiceWorkerReady();
      
      if (!registration) {
        this.logger.debug("Service worker not ready, skipping update check");
        return false;
      }

      // Trigger update check
      this.logger.debug("Triggering update check");
      await registration.update();
      this.logger.debug("Update check completed");

      // Check if there's a waiting service worker (update available)
      const hasUpdate = !!registration.waiting;

      this.logger.debug(`Has update: ${hasUpdate}`);

      return hasUpdate;
    } catch (error) {
      this.logger.debug(`Error checking for service worker version: ${error instanceof Error ? error.message : String(error)}`);
      return false;
    }
  }

  /**
   * Test connection to service worker
   */
  public async testServiceWorkerConnection(): Promise<boolean> {
    try {
      const registration = await this.getServiceWorkerReady();
      
      if (!registration) {
        this.logger.debug("Service worker not ready for connection test");
        return false;
      }

      return await new Promise<boolean>((resolve) => {
        const messageChannel = new MessageChannel();

        messageChannel.port1.onmessage = (event: MessageEvent) => {
          const data = event.data as ServiceWorkerMessage;
          if (data?.type === "PONG") {
            this.logger.debug("Service worker connected");
            resolve(true);
          } else {
            this.logger.debug("Service worker not connected");
            resolve(false);
          }
        };

        // Timeout after 5 seconds
        setTimeout(() => resolve(false), 5000);

        // Send ping message
        registration.active?.postMessage(
          { type: "PING" },
          [messageChannel.port2]
        );
      });
    } catch (error) {
      this.logger.debug(`Error testing service worker connection: ${error instanceof Error ? error.message : String(error)}`);
      return false;
    }
  }

  /**
   * Force update the service worker and reload the application
   */
  public async forceServiceWorkerUpdate(): Promise<void> {
    try {
      const registration = await this.getServiceWorkerReady();
      
      if (!registration) {
        this.logger.debug("Service worker not ready for force update");
        return;
      }

      this.logger.debug("Force service worker update");
      registration.active?.postMessage({ type: "FORCE_UPDATE" });

    } catch (error) {
      // Silent error handling
    }
  }

  /**
  * Request current version information from service worker
  */
  public async getServiceWorkerVersion(): Promise<ServiceWorkerUpdateInfo | null> {
    try {
      const registration = await this.getServiceWorkerReady();
      
      if (!registration) {
        this.logger.debug("Service worker not ready for version request");
        return null;
      }

      return await new Promise<ServiceWorkerUpdateInfo | null>((resolve) => {
        const messageChannel = new MessageChannel();

        messageChannel.port1.onmessage = (event: MessageEvent) => {
          const data = event.data as ServiceWorkerMessage;
          if (data?.type === "VERSION_INFO") {
            this.logger.debug("Version info received");
            resolve({
              version: data.version ?? "unknown",
              buildTime: data.buildTime ?? "unknown",
              updateAvailable: false,
              isInstalling: false,
              isActivated: true,
              cachesCleared: 0,
              features: {
                pushNotifications: true,
                offlineSupport: true,
                cacheManagement: true
              }
            });
          } else {
            this.logger.debug("Version info not received");
            resolve(null);
          }
        };

        // Timeout after 5 seconds
        setTimeout(() => resolve(null), 5000);

        // Send version request
        registration.active?.postMessage(
          { type: "GET_VERSION" },
          [messageChannel.port2]
        );
      });
    } catch (error) {
      this.logger.debug(`Error getting service worker version: ${error instanceof Error ? error.message : String(error)}`);
      return null;
    }
  }

  /**
   * Pulisce le risorse quando il servizio viene distrutto
   */
  destroy(): void {
    this.isInitialized = false;
  }
}
